import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import axiosInstance from "../../../app/axiosInstance";
import { prepareHeaders } from "../../auth/slice/AuthSlice";
import { handleApiError } from "../../common/utils/ErrorHandler";
import { PER_PAGE_CONTACT } from "../../../constants/pagination";

const contactUrl = "api/admin/contact-us/index";

export const fetchContactList = createAsyncThunk(
  "contacts/fetchContactList",
  async (
    { page = 1, filter = "", perPage = PER_PAGE_CONTACT },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const response = await axiosInstance.get(`${contactUrl}`, {
        params: { page, per_page: perPage, search: filter },
        headers: prepareHeaders(state),
      });
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

const ContactSlice = createSlice({
  name: "contact",
  initialState: {
    loading: false,
    contacts: [],
    contact: null,
    pageCount: 0,
    currentPage: 0,
    filter: "",
    perPage: PER_PAGE_CONTACT,
    error: "",
  },
  reducers: {
    /**
     * Sets the filter state and resets the current page to 0
     * @param {string} action.payload The filter string
     */
    setFilter: (state, action) => {
      state.filter = action.payload;
      state.currentPage = 0;
    },
    /**
     * Sets the current page of the contact list
     * @param {number} action.payload The page number to set
     */
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    /**
     * Sets the number of contacts to display per page and resets the current page to 0.
     * @param {number} action.payload The number of contacts to display per page
     */

    setPerPage: (state, action) => {
      state.perPage = action.payload;
      state.currentPage = 0;
    },
    /**
     * Clears the error state of the contact list
     */
    clearError: (state) => {
      state.error = "";
    },
  },
  /**
   * Extra reducers for this slice, used to handle asynchronous actions
   * @param {Object} builder The builder object from `createSlice`
   * @returns {Object} An object with extra reducers
   */
  extraReducers: (builder) => {
    builder
      // fetch contacts
      .addCase(fetchContactList.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchContactList.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload.payload.data;
        state.pageCount = action.payload.payload.last_page;
        state.currentPage = action.payload.payload.current_page - 1;
      })
      .addCase(fetchContactList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilter, setPage, setPerPage, clearError } =
  ContactSlice.actions;

export default ContactSlice.reducer;
